import * as React from 'react'
import { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, InputAdornment, Paper, TextField, Typography } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import { ReservationContext } from '../../context/ReservationContext'

export function PersonalInfoForm({ setPreviousStepCompleted, handleNext }) {
  const { firstName, setFirstName, lastName, setLastName, phone, setPhone, email, setEmail } = useContext(ReservationContext)
  const [formData, setFormData] = useState({
    firstName: firstName,
    lastName: lastName,
    phone: phone,
    email: email
  })

  useEffect(() => {
    setFormData({
      firstName,
      lastName,
      phone,
      email
    })
  }, [firstName, lastName, phone, email])

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    email: false
  })

  const [errorMessages, setErrorMessages] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  })

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    email: false
  })

  // Validate email format
  const validateEmail = (email) => {
    // More comprehensive email validation
    const re =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    return re.test(email)
  }

  useEffect(() => {
    if (
      !errors.firstName &&
      formData.firstName.length > 0 &&
      !errors.lastName &&
      formData.lastName.length > 0 &&
      !errors.phone &&
      formData.phone.length > 0 &&
      !errors.email &&
      formData.email.length > 0
    ) {
      setFirstName(formData.firstName)
      setLastName(formData.lastName)
      setPhone(formData.phone)
      setEmail(formData.email)
      setPreviousStepCompleted(true)
    } else {
      setPreviousStepCompleted(false)
    }
  }, [errors, handleNext, setPreviousStepCompleted, touched, formData, setFirstName, setLastName, setPhone, setEmail])

  // Validate phone number (simple international format)
  const validatePhone = (phone) => {
    const re = /^\+?[0-9\s\-()]{9,}$/
    return re.test(phone)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false
      }))
      setErrorMessages((prev) => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    setTouched((prev) => ({
      ...prev,
      [name]: true
    }))

    // Validate on blur
    if (value.trim() === '') {
      setErrors((prev) => ({
        ...prev,
        [name]: true
      }))
      setErrorMessages((prev) => ({
        ...prev,
        [name]: 'Ce champ est requis'
      }))
    } else if (name === 'email' && !validateEmail(value)) {
      setErrors((prev) => ({
        ...prev,
        email: true
      }))
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Veuillez entrer une adresse email valide'
      }))
    } else if (name === 'phone' && !validatePhone(value)) {
      setErrors((prev) => ({
        ...prev,
        phone: true
      }))
      setErrorMessages((prev) => ({
        ...prev,
        phone: 'Veuillez entrer un numéro de téléphone valide (ex: +33123456789)'
      }))
    } else if ((name === 'firstName' || name === 'lastName') && value.length > 50) {
      setErrors((prev) => ({
        ...prev,
        [name]: true
      }))
      setErrorMessages((prev) => ({
        ...prev,
        [name]: `Le ${name === 'firstName' ? 'prénom' : 'nom'} ne peut dépasser 50 caractères`
      }))
    }
  }

  const validateForm = () => {
    let isValid = true
    const newErrors = { ...errors }
    const newErrorMessages = { ...errorMessages }

    // Check required fields
    Object.keys(formData).forEach((key) => {
      if (formData[key].trim() === '') {
        newErrors[key] = true
        newErrorMessages[key] = 'Ce champ est requis'
        isValid = false
      }
    })

    // Validate name length (max 50 characters)
    if (formData.firstName && formData.firstName.length > 50) {
      newErrors.firstName = true
      newErrorMessages.firstName = 'Le prénom ne peut dépasser 50 caractères'
      isValid = false
    }

    if (formData.lastName && formData.lastName.length > 50) {
      newErrors.lastName = true
      newErrorMessages.lastName = 'Le nom ne peut dépasser 50 caractères'
      isValid = false
    }

    // Validate email
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = true
      newErrorMessages.email = 'Veuillez entrer une adresse email valide'
      isValid = false
    }

    // Validate phone
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = true
      newErrorMessages.phone = 'Veuillez entrer un numéro de téléphone valide (ex: +33123456789)'
      isValid = false
    }

    setErrors(newErrors)
    setErrorMessages(newErrorMessages)
    return isValid
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (validateForm()) {
      setPreviousStepCompleted(true)
      handleNext(formData)
    }
  }

  return (
    <Paper
      elevation={3}
      sx={{
        minHeight: '400px',
        maxHeight: { xs: '50vh', sm: '60vh' },
        overflowY: 'auto',
        p: { xs: 1, sm: 2 },
        borderRadius: 10
      }}
    >
      <Typography variant='h5' data-testid='personal-info-heading' sx={{ mb: 1, fontWeight: 'bold', color: 'title.main' }}>
        4. Informations personnelles
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              fullWidth
              name='firstName'
              label='Prénom'
              value={formData.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.firstName && touched.firstName}
              helperText={errors.firstName && touched.firstName ? errorMessages.firstName : ''}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <PersonIcon color={errors.firstName && touched.firstName ? 'error' : 'action'} />
                    </InputAdornment>
                  )
                }
              }}
              variant='outlined'
            />

            <TextField
              fullWidth
              name='lastName'
              label='Nom'
              value={formData.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.lastName && touched.lastName}
              helperText={errors.lastName && touched.lastName ? errorMessages.lastName : ''}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <PersonIcon color={errors.lastName && touched.lastName ? 'error' : 'action'} />
                    </InputAdornment>
                  )
                }
              }}
              variant='outlined'
            />
          </Box>

          <TextField
            fullWidth
            name='email'
            label='Email'
            type='email'
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.email && touched.email}
            helperText={errors.email && touched.email ? errorMessages.email : ''}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <EmailIcon color={errors.email && touched.email ? 'error' : 'action'} />
                  </InputAdornment>
                )
              }
            }}
            variant='outlined'
          />

          <TextField
            fullWidth
            name='phone'
            label='Téléphone'
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.phone && touched.phone}
            helperText={errors.phone && touched.phone ? errorMessages.phone : ''}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <PhoneIcon color={errors.phone && touched.phone ? 'error' : 'action'} />
                  </InputAdornment>
                )
              }
            }}
            variant='outlined'
            placeholder='+32123456789'
          />
        </Box>
      </form>
    </Paper>
  )
}

PersonalInfoForm.propTypes = {
  setPreviousStepCompleted: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
}
